import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  Alert, 
  CircularProgress, 
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Switch,
  FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import { apiService, SensorField } from '../../services/ApiService';
import { useSondeContext } from '../../context/SondeContext';
import EmailConfigPanel from '../alerts/EmailConfigPanel';

// Interface pour un seuil d'alerte dynamique
interface FieldThreshold {
  field_code: string;
  field_name: string;
  unit: string;
  min: number;
  max: number;
  isactive: boolean;
}

// Interface pour les settings d'un boîtier avec champs dynamiques
interface BoitierSettings {
  boitier_id: number;
  fields: FieldThreshold[];
}

// Couleurs pour les champs dynamiques
const fieldColors = ['warning.main', 'info.main', 'success.main', 'error.main', 'secondary.main', 'primary.main'];

const SettingsView: React.FC = () => {
  const { bains: boitiers } = useSondeContext();
  
  // Settings dynamiques par boîtier
  const [settings, setSettings] = useState<BoitierSettings[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error'}>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [expanded, setExpanded] = useState<number | false>(false);

  // Gérer le changement de l'accordéon
  const handleAccordionChange = (boitierId: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? boitierId : false);
  };

  // Charger les paramètres dynamiques pour chaque boîtier
  useEffect(() => {
    const fetchSettings = async () => {
      if (boitiers.length === 0) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const loadedSettings: BoitierSettings[] = [];
        
        for (const boitier of boitiers) {
          const boitierId = boitier.boitier_id;
          try {
            // Charger les champs du device
            const deviceFields = await apiService.getDeviceFields(boitierId);
            // Charger les seuils existants
            const alertData = await apiService.getAlertSettings(boitierId);
            
            // Construire les seuils pour chaque champ
            const fields: FieldThreshold[] = deviceFields.map((field: SensorField) => {
              // Chercher le seuil existant pour ce champ
              const existingThreshold = (alertData as any)[field.field_code];
              return {
                field_code: field.field_code,
                field_name: field.field_name,
                unit: field.unit || '',
                min: existingThreshold?.min ?? field.default_min ?? 0,
                max: existingThreshold?.max ?? field.default_max ?? 100,
                isactive: existingThreshold?.isactive ?? false
              };
            });
            
            loadedSettings.push({ boitier_id: boitierId, fields });
          } catch (err) {
            // Erreur silencieuse pour le boîtier
            // Fallback: champs par défaut si erreur
            loadedSettings.push({
              boitier_id: boitierId,
              fields: [
                { field_code: 'temp', field_name: 'Température', unit: '°C', min: 20, max: 35, isactive: false },
                { field_code: 'pH', field_name: 'pH', unit: '', min: 6.5, max: 8.5, isactive: false },
                { field_code: 'cond', field_name: 'Conductivité', unit: 'mS/cm', min: 2, max: 10, isactive: false }
              ]
            });
          }
        }
        
        loadedSettings.sort((a, b) => a.boitier_id - b.boitier_id);
        setSettings(loadedSettings);
        setError(null);
      } catch (err: any) {
        // Erreur silencieuse
        setError(`Impossible de charger les paramètres : ${err.message || 'Erreur inconnue'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [boitiers]);

  // Mettre à jour un seuil dynamique
  const handleThresholdChange = (boitierId: number, fieldCode: string, field: 'min' | 'max', value: string) => {
    const newValue = parseFloat(value);
    if (isNaN(newValue)) return;
    
    setSettings(prevSettings => prevSettings.map(setting => {
      if (setting.boitier_id === boitierId) {
        return {
          ...setting,
          fields: setting.fields.map(f => 
            f.field_code === fieldCode ? { ...f, [field]: newValue } : f
          )
        };
      }
      return setting;
    }));
  };

  // Activer/désactiver un seuil
  const handleToggleActive = (boitierId: number, fieldCode: string) => {
    setSettings(prevSettings => prevSettings.map(setting => {
      if (setting.boitier_id === boitierId) {
        return {
          ...setting,
          fields: setting.fields.map(f => 
            f.field_code === fieldCode ? { ...f, isactive: !f.isactive } : f
          )
        };
      }
      return setting;
    }));
  };

  // Sauvegarder les paramètres (format dynamique)
  const handleSaveSettings = async (boitierId: number) => {
    try {
      setSaving(true);
      const boitierSettings = settings.find(s => s.boitier_id === boitierId);
      
      if (!boitierSettings) {
        throw new Error(`Paramètres introuvables pour le boîtier ${boitierId}`);
      }
      
      // Vérifier les valeurs
      for (const field of boitierSettings.fields) {
        if (field.isactive && field.min >= field.max) {
          throw new Error(`Les seuils de ${field.field_name} sont invalides (min >= max)`);
        }
      }

      // Construire l'objet de settings au format API
      const settingsPayload: any = { boitier_id: boitierId };
      for (const field of boitierSettings.fields) {
        settingsPayload[field.field_code] = {
          min: field.min,
          max: field.max,
          isactive: field.isactive
        };
      }

      await apiService.updateAlertSettings(boitierId, settingsPayload);
      
      setSnackbar({
        open: true,
        message: `Les paramètres du boîtier ${boitierId} ont été sauvegardés avec succès.`,
        severity: 'success'
      });
    } catch (err: any) {
      // LOT 4 — afficher le motif de refus renvoyé par le serveur.
      //
      // Depuis D-202/D-203, l'API refuse en 422 un seuil hors bornes ou hors
      // du domaine physique de la grandeur (un pH de -500 était accepté et
      // enregistré, et plus aucune alerte pH ne pouvait se déclencher).
      // Sans cette extraction, `err.message` d'axios afficherait « Request
      // failed with status code 422 » : l'exploitant verrait un refus sans
      // savoir quel champ corriger, et conclurait à une panne.
      const detail = err?.response?.data?.detail;
      let message: string | undefined;
      if (Array.isArray(detail)) {
        message = detail.map((e: any) => e?.msg).filter(Boolean).join(' · ');
      } else if (typeof detail === 'string') {
        message = detail;
      } else if (detail?.message) {
        message = detail.message;
      }
      setSnackbar({
        open: true,
        message: message || err.message || 'Une erreur est survenue lors de la sauvegarde.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const isValidRange = (min: number, max: number) => min < max;

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Paramètres des alertes
      </Typography>
      
      {/* Configuration email */}
      <EmailConfigPanel />
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : settings.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Aucun boîtier configuré. Ajoutez des boîtiers dans la section « Gestion boîtiers » pour configurer les alertes.
        </Alert>
      ) : (
        <Box>
          {settings.map((boitierSetting) => (
            <Accordion
              key={boitierSetting.boitier_id}
              expanded={expanded === boitierSetting.boitier_id}
              onChange={handleAccordionChange(boitierSetting.boitier_id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  {(() => {
                    const boitierInfo = boitiers.find(b => b.boitier_id === boitierSetting.boitier_id);
                    // Le nom contient déjà « Boîtier #X - ... », pas besoin de dupliquer
                    return boitierInfo?.name || `Boîtier #${boitierSetting.boitier_id}`;
                  })()}
                </Typography>
              </AccordionSummary>
              
              <AccordionDetails>
                <Grid container spacing={3}>
                  {/* Affichage dynamique de chaque champ */}
                  {boitierSetting.fields.map((field, index) => (
                    <Grid item xs={12} md={4} key={field.field_code}>
                      <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <SettingsIcon sx={{ mr: 1, color: fieldColors[index % fieldColors.length] }} />
                          <Typography variant="h6">{field.field_name}</Typography>
                        </Box>
                        
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(field.isactive)}
                              onChange={() => handleToggleActive(boitierSetting.boitier_id, field.field_code)}
                              color="primary"
                            />
                          }
                          label="Activer les alertes"
                        />
                        
                        <Box sx={{ mt: 2 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                label={`Min${field.unit ? ` (${field.unit})` : ''}`}
                                type="number"
                                value={field.min}
                                onChange={(e) => handleThresholdChange(boitierSetting.boitier_id, field.field_code, 'min', e.target.value)}
                                disabled={!field.isactive}
                                error={!isValidRange(field.min, field.max)}
                                helperText={!isValidRange(field.min, field.max) ? "Min doit être < Max" : ""}
                                InputProps={{ inputProps: { step: 0.5 } }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                label={`Max${field.unit ? ` (${field.unit})` : ''}`}
                                type="number"
                                value={field.max}
                                onChange={(e) => handleThresholdChange(boitierSetting.boitier_id, field.field_code, 'max', e.target.value)}
                                disabled={!field.isactive}
                                error={!isValidRange(field.min, field.max)}
                                helperText={!isValidRange(field.min, field.max) ? "Max doit être > Min" : ""}
                                InputProps={{ inputProps: { step: 0.5 } }}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                
                <Box sx={{ mt: 3, textAlign: 'right' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSaveSettings(boitierSetting.boitier_id)}
                    disabled={saving}
                  >
                    {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsView;
